/* eslint-disable no-console */
import { SIGNUP_SCREEN } from "./types";

import { ChangeEvent, useCallback, useEffect, useState, useRef } from "react";
import { useRecoilState } from "recoil";

import { shallowCopy } from "utils";

import {
  CreateUserRespState,
  CurrentMethodState,
  IsTermAndConditionCheckedState,
  SignUpCurrentModalState,
  SignUpFormInputsState,
  SignUpScreenState,
} from "./state";
import {
  db,
  ref as firebaseRef,
  onChildAdded,
  query,
  limitToLast,
} from "../../../firebase";
import { useNetwork, useInstance, useWebAuthn } from "hooks";
import { API_URL } from "constants/api-url";
import { ENVS } from "constants/common";
import GlobalInstanceManager from "instance-manger";
// import { IFormInput } from "global-states";
import { ERRORS, ErrorTypes } from "constants/errors";
import { useSignin } from "views/signin";
import { ISSKIPWEBAUTHN } from "../constants";

let isSignupCompleted = false;
let popupRef: Window | null = null;
// Custom hook for managing signup form state and logic
export const useSignUp = () => {
  const [loading, setLoading] = useState(false);
  const ref = useRef<any>({});
  const [loadingRegisterationTracking, setLoadingRegisterationTracking] =
    useState({
      label: "",
      loading: false,
    });

  // Smart phone input refs for debounced formatting
  const formatTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRawValueRef = useRef<{ [key: number]: string }>({});
  const isUserTypingRef = useRef<{ [key: number]: boolean }>({});
  const lastFormattedValueRef = useRef<{ [key: number]: string }>({});
  const [isCardLoading, setIsCardLoading] = useState(false);
  // State to manage the current step in the sign-up process
  const [currentStep, setCurrentStep] = useRecoilState(SignUpScreenState);
  const [currentModal, setCurrentModal] = useRecoilState(
    SignUpCurrentModalState
  );

  const { manageDefaultUser: manageSigninUser } = useSignin();

  // State for holding form inputs
  const [form, setForm] = useRecoilState(SignUpFormInputsState);
  // State for managing whether terms and conditions checkbox is checked
  const [checked, setChecked] = useRecoilState(IsTermAndConditionCheckedState);

  // State for Create User Resp
  const [userResp, setUserResp] = useRecoilState(CreateUserRespState);

  const [currentMethod, setCurrentMethod] = useRecoilState(CurrentMethodState);

  const { create } = useWebAuthn();
  //Hooks for instance
  const { instanceDetails, env, currentScreen } = useInstance();

  /**
   * Helper function to filter form fields based on scope configuration
   * Removes fields that are not included in instanceDetails.signUp.scope
   */
  const filterFormByScope = useCallback(
    (formArray: any[]) => {
      const scope = instanceDetails?.signUp?.scope;
      if (!scope || scope.length === 0) {
        return formArray;
      }

      return formArray.filter((field) => {
        // Always include firstName and lastName (they're not scope-dependent)
        if (field.name === "firstName" || field.name === "lastName") {
          return true;
        }
        // Filter phone and email based on scope
        return scope.includes(field.name);
      });
    },
    [instanceDetails?.signUp?.scope]
  );

  // Effect to filter form when scope changes
  useEffect(() => {
    if (instanceDetails?.signUp?.scope) {
      setForm((prevForm) => filterFormByScope(prevForm));
    }
  }, [instanceDetails?.signUp?.scope, setForm, filterFormByScope]);

  const { get: socialAuth } = useNetwork();

  const { onErrorEvent, onSuccessEvent, onStepChangeEvent, webcomponentProps } =
    GlobalInstanceManager;

  const {
    instance_id,
    auto_navigate,
    microsoft_redirect_url,
    facebook_redirect_url,
    apple_redirect_url,
    google_redirect_url,
  } = webcomponentProps;

  const handleSSO = async (param: any) => {
    if (param.errorCode) {
      onErrorEvent({ ...param, type: "sso-data" });
      if (typeof popupRef?.close === "function") {
        try {
          popupRef.close();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log({ error });
        }
      }
      if (param.errorCode === "EMAIL_EXISTS" && auto_navigate) {
        await manageSigninUser({ email: param?.email ?? "" });
      } else {
        onErrorEvent(ERRORS[param.errorCode as keyof ErrorTypes]);
      }
    } else if (Object.keys(param).length > 0 && param.userId) {
      onStepChangeEvent({ ...param, type: "sso-data" });
      const data = [];

      // Create a shallow copy of the form to avoid direct mutation
      const newArr = shallowCopy(form);

      // Iterate over the form array and update values based on the param data
      for (let i = 0; i < newArr.length; i++) {
        if (param[newArr[i].name]) {
          const payload = {
            ...newArr[i],
            value: param[newArr[i].name], // Update the value from param
            disabled: true, // Disable the input
          };
          data.push(payload);
        } else {
          data.push(newArr[i]);
        }
      }
      // Update form state with the new data array
      setForm(filterFormByScope(data));

      const email = { id: param.email, isVerified: true } as any;
      // Set user response with userId from param
      ref.current = { ...userResp, _id: param.userId, email };
      setUserResp({ ...userResp, _id: param.userId, email });
      // sendMessage("signUp", { userId: param.userId });

      // Set the current step in the signup process
      setCurrentStep(SIGNUP_SCREEN.ACCOUNT_DETAILS);

      // Close the popup if the close function is available
      if (typeof popupRef?.close === "function") {
        try {
          popupRef.close();
        } catch (error) {
          console.log({ error });
        }
      }
    }
  };
  /**
   * useEffect hook to handle incoming messages and update form state accordingly.
   */
  useEffect(() => {
    // Message event listener function
    const messageListener = async (event: MessageEvent) => {
      handleSSO(event.data);
    };

    // Add event listener for messages
    window.addEventListener("message", messageListener);

    // Cleanup function to remove event listener on component unmount
    return () => {
      window.removeEventListener("message", messageListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //hooks for submit credential
  const { post: submitCredential, loading: loadingSubmitCredential } =
    useNetwork();

  //hooks for submit credential
  const { post: resend, loading: loadingResend } = useNetwork();

  const { patch: submitSessionSuccess } = useNetwork();
  //hooks for submitRegistration credential
  const { patch: submitReg, loading: loadingRegistration } = useNetwork();

  const { post: getBiometricDetails } = useNetwork();

  const getWebAuthnOptions = useCallback(async () => {
    try {
      onStepChangeEvent({
        type: "setting-biometric",
        from: "signup",
      });
      const resp = await getBiometricDetails(API_URL.PASSKEY, {
        type: "registrationOpts",
        payload: {
          rpId: window.location.origin,
          rpName: "Domain",
          userId: userResp._id,
        },
      });

      if (resp.statusCode === 200) {
        resp.registrationOptions.authenticatorSelection = {
          residentKey: "preferred",
          userVerification: "preferred",
          requireResidentKey: false,
        };
        onStepChangeEvent({
          ...resp,
          type: "webAuthn",
          from: "signup",
        });
        const response: any = await create(resp.registrationOptions).catch(
          () => {
            setLoadingRegisterationTracking({
              label: "",
              loading: false,
            });
            setIsCardLoading(false);
          }
        );

        if (!response.isError) {
          await getBiometricDetails(API_URL.PASSKEY, {
            type: "verifyRegistration",
            payload: {
              registrationOptResponse: {
                ...response,
              },
              userId: userResp._id,
            },
          });
          onStepChangeEvent({
            type: "success-setting-biometric",
            from: "signup",
          });

          return { apiResp: resp, webauthnResp: response };
        }
      } else {
        onErrorEvent({
          ...resp,
          type: "failed-setting-biometric",
          from: "signup",
        });
        return resp;
      }
    } catch (error) {
      onErrorEvent({
        type: "failed-setting-biometric",
        from: "signup",
        error: error,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getBiometricDetails, userResp._id]);

  const handleOnCompleteSignUpProcess = useCallback(
    async (sessionId?: string) => {
      if (instanceDetails.methods?.biometric && !ISSKIPWEBAUTHN) {
        setLoadingRegisterationTracking({
          label: "Setting Biometric ",
          loading: true,
        });
        await getWebAuthnOptions()
          .catch(() => {
            setLoadingRegisterationTracking({
              label: "",
              loading: false,
            });
            setIsCardLoading(false);
          })
          .finally(() => {
            setLoadingRegisterationTracking({
              label: "",
              loading: false,
            });
          });
      }

      if (userResp._id && sessionId) {
        await submitSessionSuccess(`${API_URL.USER}/${userResp._id}`, {
          sessionId,
        });
      }
    },
    [
      getWebAuthnOptions,
      instanceDetails.methods?.biometric,
      submitSessionSuccess,
      userResp,
    ]
  );

  /**
   * Smart phone input handler that respects user intent (same logic as combined input)
   */
  const handleSmartPhoneInput = useCallback(
    (value: string, index: number, field: any, forceFormat = false) => {
      // Clear any existing timeout for this field
      if (formatTimeoutRef.current) {
        clearTimeout(formatTimeoutRef.current);
      }

      // Extract digits only for comparison
      const currentDigits = value.replace(/\D/g, "");
      const lastDigits = (lastRawValueRef.current[index] || "").replace(
        /\D/g,
        ""
      );
      const lastFormattedDigits = (
        lastFormattedValueRef.current[index] || ""
      ).replace(/\D/g, "");

      // Detect if user is deleting digits (important for preventing auto-restore)
      const isDeleting =
        currentDigits.length < lastDigits.length ||
        currentDigits.length < lastFormattedDigits.length;

      // Mark as user typing
      isUserTypingRef.current[index] = true;

      // Store current raw value
      lastRawValueRef.current[index] = value;

      // Create immediate form (unformatted during typing, unless forced)
      let displayValue = value;
      let validationInput = value;

      // Only format if forced (like country code change) or if it's not user typing
      if (forceFormat || !isUserTypingRef.current[index]) {
        if (field.formatter && currentDigits.length > 0) {
          const phoneObj = form.find((el) => el.name === "phone");
          const formattedData = field.formatter({
            value,
            countryCode: phoneObj?.countryCode ?? "+1",
          });
          displayValue = formattedData.formatted ?? value;
          validationInput = formattedData.validationInput ?? value;
          lastFormattedValueRef.current[index] = displayValue;
        }
      }

      setForm(() => {
        const newObj = shallowCopy(form) ?? form;
        if (newObj[index]) {
          newObj[index]["value"] = displayValue;

          // Validate - always use digits only for phone validation
          if (field.validation) {
            let validationTarget = validationInput;

            // For phone fields, use smart validation logic - only validate meaningful input
            if (field.name === "phone") {
              // Extract digits only from the raw input for validation
              const digitsOnly = value.replace(/\D/g, "");

              // Smart validation: Don't validate until user has entered substantial input
              if (digitsOnly.length === 0) {
                // Empty phone number - no validation error
                // eslint-disable-next-line no-console
                console.log("📱 Account Details empty - skipping validation");
                delete newObj[index]["isError"];
                newObj[index]["errorMessage"] = "";
                return filterFormByScope(newObj);
              } else if (digitsOnly.length > 0 && digitsOnly.length < 7) {
                // User still typing - don't validate until they have meaningful input (7+ digits)
                delete newObj[index]["isError"];
                newObj[index]["errorMessage"] = "";
                return filterFormByScope(newObj);
              } else {
                // Only validate when user has 7+ digits (more meaningful)
                validationTarget = digitsOnly;
              }
            }

            const { isError, errorMessage } =
              field.validation(validationTarget);
            newObj[index]["isError"] = isError;
            newObj[index]["errorMessage"] = errorMessage;

            // eslint-disable-next-line no-console
          }
        }
        return filterFormByScope(newObj);
      });

      // Set timeout for formatting only if user is typing and not deleting
      if (
        !forceFormat &&
        isUserTypingRef.current[index] &&
        !isDeleting &&
        currentDigits.length >= 3
      ) {
        formatTimeoutRef.current = setTimeout(() => {
          // Double-check the user stopped typing and value hasn't changed
          if (lastRawValueRef.current[index] === value) {
            isUserTypingRef.current[index] = false;
            handleSmartPhoneInput(value, index, field, true);
          }
        }, 500);
      }
    },
    [form, setForm, filterFormByScope]
  );

  /**
   * Handles changes in form inputs with smart phone formatting
   * @param {ChangeEvent<HTMLInputElement>} event - Change event from form input
   */
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target ?? {}; // Extract value from input event
      const { index, validation, formatter } = event as any; // Extract index and validation function from event

      const currentField = form[index];

      // If this is a phone field, use smart phone input handler
      if (currentField && currentField.name === "phone" && formatter) {
        handleSmartPhoneInput(value, index, currentField);
        return;
      }

      // For non-phone fields, use immediate update (email, name, etc.)
      let inputVal = value;
      let validationInput = value;

      if (formatter && currentField && currentField.name !== "phone") {
        const phoneObj = form.find((el) => el.name === "phone");
        const newData = formatter({
          value,
          countryCode: phoneObj?.countryCode ?? "+1",
        });
        inputVal = newData.formatted ?? value;
        validationInput = newData.validationInput ?? value;
      }

      setForm(() => {
        const newObj = shallowCopy(form) ?? form; // Create a shallow copy of the form state
        if (newObj[index]) {
          // Check if the form element at index exists
          newObj[index]["value"] = inputVal; // Update the value of the form element
          if (validation) {
            // If validation function is provided
            const { isError, errorMessage } = validation(validationInput); // Validate the input value

            newObj[index]["isError"] = isError; // Set error status
            newObj[index]["errorMessage"] = errorMessage; // Set error message
          }
        }
        return filterFormByScope(newObj); // Return updated form state
      });
    },
    [form, setForm, handleSmartPhoneInput, filterFormByScope] // Dependency array to ensure the callback updates when form changes
  );

  /**
   * Handles changes in country code for form inputs with smart formatting
   * @param {string} value - Country code value
   * @param {number} index - Index of the form element to update
   */
  const handleChangeCountry = useCallback(
    (value: string, index: number) => {
      const currentField = form[index];

      // If this is a phone field, always update the country code
      if (currentField && currentField.name === "phone") {
        // eslint-disable-next-line no-console
        console.log(
          "🌍 Changing country code to:",
          value,
          "for field:",
          currentField.name
        );

        // Update country code and handle phone reformatting in one go
        setForm(() => {
          const newObj = shallowCopy(form) ?? form;
          if (newObj[index]) {
            const oldCountryCode = newObj[index].countryCode;
            newObj[index].countryCode = value;
            // eslint-disable-next-line no-console
            console.log(
              "✅ Country code updated in form:",
              oldCountryCode,
              "→",
              newObj[index].countryCode
            );

            // If there's a phone value, reformat it immediately with the new country code
            if (newObj[index].value && newObj[index].formatter) {
              // eslint-disable-next-line no-console
              console.log(
                "🔄 Reformatting phone with new country code:",
                newObj[index].value
              );

              const formattedData = newObj[index].formatter({
                value: newObj[index].value,
                countryCode: value, // Use the new country code
              });

              if (formattedData.formatted) {
                newObj[index].value = formattedData.formatted;
                // eslint-disable-next-line no-console
                console.log("✨ Phone reformatted:", formattedData.formatted);
              }

              // Re-validate with new formatting if validation exists
              if (newObj[index].validation) {
                const validationInput =
                  formattedData.validationInput ??
                  formattedData.formatted ??
                  newObj[index].value;
                const digitsOnly = validationInput.replace(/\D/g, "");

                // Only validate if we have substantial input (7+ digits)
                if (digitsOnly.length >= 7) {
                  const { isError, errorMessage } =
                    newObj[index].validation(digitsOnly);
                  newObj[index].isError = isError;
                  newObj[index].errorMessage = errorMessage;
                } else {
                  delete newObj[index].isError;
                  newObj[index].errorMessage = "";
                }
              }
            }
          }
          return newObj;
        });
        return;
      }

      // For non-phone fields, use immediate update
      setForm(() => {
        const newObj = shallowCopy(form) ?? form; // Create a shallow copy of the form state
        if (newObj[index]) {
          // Check if the form element at index exists
          const currentFieldCopy = newObj[index];

          // Update the country code
          currentFieldCopy.countryCode = value;

          // If this is a phone field with existing value and has formatter, reformat with new country code
          if (
            currentFieldCopy.name === "phone" &&
            currentFieldCopy.value &&
            currentFieldCopy.formatter
          ) {
            const reformattedData = currentFieldCopy.formatter({
              value: currentFieldCopy.value,
              countryCode: value,
            });

            // Update the formatted value
            currentFieldCopy.value =
              reformattedData.formatted ?? currentFieldCopy.value;

            // Re-validate with new formatting if validation exists
            if (currentFieldCopy.validation) {
              const validationInput =
                reformattedData.validationInput ??
                reformattedData.formatted ??
                currentFieldCopy.value;
              const { isError, errorMessage } =
                currentFieldCopy.validation(validationInput);
              currentFieldCopy.isError = isError;
              currentFieldCopy.errorMessage = errorMessage;
            }
          }
        }
        return newObj; // Return updated form state
      });
    },
    [form, setForm] // Dependency array to ensure the callback updates when form changes
  );

  /**
   * Placeholder function for submitting credentials (currently not implemented)
   * @returns {string} - Empty string (placeholder)
   */
  const submitCred = async (
    onSuccess?: (e: any) => void,
    onReject?: (e: any) => void,
    newSignForm?: any
  ): Promise<any> => {
    // Reset token generation flag for new authentication attempt
    GlobalInstanceManager.resetTokenGenerationFlag();
    
    const userInfo: any = {}; // Object to hold user information
    const updatedForm = JSON.parse(JSON.stringify(newSignForm || form));

    // Check if form has elements
    for (let i = 0; i < updatedForm?.length; i++) {
      // Iterate over form elements
      const { name, value, countryCode, isCountryCodeVisible } =
        updatedForm[i] ?? {}; // Extract name and value from form element
      if (name && value) {
        if (isCountryCodeVisible) {
          Object.assign(userInfo, { ["countryCode"]: countryCode });
        }

        // Check if name and value are present
        Object.assign(userInfo, { [name]: value }); // Add name-value pair to userInfo
      }
    }

    const payload = {
      email: userInfo.email || undefined,
      instanceId: instance_id ?? "",
      phone: userInfo.phone?.replace(/\D/g, "") || undefined,
      countryCode: userInfo.countryCode || undefined,
    };
    setCurrentMethod(payload?.email ? "email" : "phone");
    const resp = await submitCredential(API_URL.USER, payload);

    if (resp?.statusCode !== 200) {
      if (
        resp.message === "User already exist" &&
        webcomponentProps.auto_navigate
      ) {
        await manageSigninUser(payload);
      }
      onErrorEvent({ ...resp, payload });
      onReject?.({ ...resp, data: payload });
    } else {
      ref.current = resp;
      setUserResp(resp);
      setForm((prev) => {
        const newObj = shallowCopy(prev);
        const updatedForm = newObj.map((el: any) => {
          if (instanceDetails.signUp?.scope.includes(el.name)) {
            return {
              ...el,
              disabled: true,
            };
          }
          return el;
        });
        return filterFormByScope(updatedForm);
      });
      // sendMessage("signUp", { userId: resp._id });
      onSuccess?.({ ...resp, data: payload });
      onStepChangeEvent({
        data: {
          ...resp,
          ...payload,
        },
        type: "cred",
      });
    }
  };

  const manageDefaultUser = async (user: any, param?: any) => {
    if (param) {
      handleSSO(param);
      return;
    }
    if (Object.keys(user || {}).length === 0) {
      return;
    }
    const newSignForm = form.map((el) => {
      return {
        ...el,
        value: user[el.name],
        ...(el.name === "phone" && {
          countryCode: user.countryCode || el.countryCode,
        }),
      };
    });
    setForm(filterFormByScope(newSignForm));
    await submitCred(
      () => {
        setCurrentModal(SIGNUP_SCREEN.VERIFY || "");
      },
      undefined,
      newSignForm
    );
  };

  /**
   * Handles form submission and simulates registration process
   */
  const handleSubmitRegistration = useCallback(async () => {
    // Reset token generation flag for new authentication attempt
    GlobalInstanceManager.resetTokenGenerationFlag();
    
    setLoadingRegisterationTracking({
      label: "Submitting Registration",
      loading: true,
    });
    setIsCardLoading(true);
    const userInfo: any = {}; // Object to hold user information

    // Check if form has elements
    for (let i = 0; i < form?.length; i++) {
      // Iterate over form elements
      const { name, value, isCountryCodeVisible, countryCode } = form[i] ?? {}; // Extract name and value from form element
      if (name && value) {
        if (isCountryCodeVisible) {
          // Check if name and value are present
          Object.assign(userInfo, {
            [name]: {
              number: value,
              countryCode,
            },
          }); // Add name-value pair to userInfo
        } else {
          // Check if name and value are present
          Object.assign(userInfo, { [name]: value }); // Add name-value pair to userInfo
        }
      }
    }
    const { firstName, lastName, phone, email } = userInfo;

    const value =
      phone?.number && !(userResp?.phone as any)?.isVerified
        ? { ...phone, number: phone.number.replace(/\D/g, "") }
        : undefined;

    const emailValue =
      email && !(userResp?.email as any)?.isVerified ? email : undefined;

    const resp = await submitReg(`${API_URL.USER}/${userResp._id}`, {
      firstName,
      lastName,
      phone: value,
      email: emailValue,
    }).catch(() => {
      setLoadingRegisterationTracking({
        label: "",
        loading: false,
      });
      setIsCardLoading(false);
    });

    if (resp?.message?.toLowerCase().includes("exist")) {
      if (value && resp.message.toLowerCase().includes("phone")) {
        onErrorEvent({ message: "Phone already exist" });
      } else if (emailValue && resp.message.toLowerCase().includes("email")) {
        onErrorEvent({ message: "Email already exists" });
      } else {
        onErrorEvent({ message: "Phone or Email already exists" });
      }
      setLoadingRegisterationTracking({
        label: "",
        loading: false,
      });
      setIsCardLoading(false);
      return;
    }

    console.log({ resp, userResp });
    ref.current = resp;
    setUserResp((prev) => ({ ...prev, ...resp }));

    if (resp?.phone?.isVerified === false) {
      // sendMessage("signUp", { userId: resp._id });
      setCurrentModal("verify");
      setCurrentMethod("phone");
    } else if (resp?.email?.isVerified === false) {
      // sendMessage("signUp", { userId: resp._id });
      setCurrentModal("verify");
      setCurrentMethod("email");
    } else if (instanceDetails.onboardingflow) {
      setCurrentModal(SIGNUP_SCREEN.ONBOARDING || "");
    } else {
      await handleOnCompleteSignUpProcess();
    }
    setLoadingRegisterationTracking({
      label: "",
      loading: false,
    });
  }, [
    userResp,
    submitReg,
    setUserResp,
    instanceDetails.onboardingflow,
    form,
    onErrorEvent,
    // sendMessage,
    setCurrentModal,
    setCurrentMethod,
    handleOnCompleteSignUpProcess,
  ]); // Dependency array to ensure the callback updates when form changes

  const handleResend = useCallback(async () => {
    const meta = {
      purpose:
        currentMethod === "phone" ? "phoneVerification" : "emailVerification",
    };

    const payload = {
      ...meta,
      userId: userResp._id,
    };
    const resp = await resend(API_URL.APPROVAL, payload);

    if (resp.statusCode !== 200) {
      onErrorEvent({
        type: "resend",
        ...resp,
      });
    } else {
      onStepChangeEvent({
        data: {
          ...resp,
          ...payload,
        },
        type: "resend",
      });
    }
  }, [currentMethod, userResp._id, resend, onStepChangeEvent, onErrorEvent]);

  const handleSocialSignUp = useCallback(
    async (el: { key: string }) => {
      // Reset token generation flag for new authentication attempt
      GlobalInstanceManager.resetTokenGenerationFlag();
      
      setLoading(true);
      let url = `${API_URL.OAUTH}/${
        el.key
      }?instanceId=${instance_id}&type=signUp${
        env === ENVS.DEV ? "&test=true" : ""
      }`;
      if (el.key === "facebook" && facebook_redirect_url) {
        url = url + `&redirect_url=${facebook_redirect_url}`;
      }
      if (el.key === "microsoft" && microsoft_redirect_url) {
        url = url + `&redirect_url=${microsoft_redirect_url}`;
      }
      if (el.key === "google" && google_redirect_url) {
        url = url + `&redirect_url=${google_redirect_url}`;
      }
      if (el.key === "apple" && apple_redirect_url) {
        url = url + `&redirect_url=${apple_redirect_url}`;
      }

      //&platform=${webcomponentProps.platform || "web"}
      const resp = await socialAuth(url);
      if (resp?.statusCode === 200 && resp?.data?.url) {
        onStepChangeEvent({
          ...resp.data,
          type: "sso",
          from: "signUp",
          key: el.key,
        });
        popupRef = window.open(resp.data.url, "popup", "width=600,height=400");
      }
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    },
    [
      apple_redirect_url,
      env,
      facebook_redirect_url,
      google_redirect_url,
      instance_id,
      microsoft_redirect_url,
      onStepChangeEvent,
      socialAuth,
    ]
  );

  const handleOnApproveStatus = async (resp: any) => {
    if (resp.status === "APPROVED") {
      // Create a shallow copy of userResp
      const newData: any = shallowCopy({ ...userResp, ...ref.current });

      // Determine the key to update based on the purpose of the response
      const key = resp.purpose === "emailVerification" ? "email" : "phone";

      // If the key exists in newData, mark it as verified
      if (newData[key]) {
        newData[key].isVerified = true;
      }

      const { email, firstName, lastName, phone } = newData;

      // Update the current step to the VERIFIED screen
      setCurrentModal(SIGNUP_SCREEN.VERIFIED);

      if (!instanceDetails.onboardingflow) {
        setTimeout(() => {
          setCurrentModal(undefined);
        }, 1000);
      }

      if (
        ((email?.isVerified && phone?.isVerified) ||
          instanceDetails.signUp?.scope.length === 1) &&
        firstName &&
        lastName
      ) {
        if (instanceDetails.onboardingflow) {
          setTimeout(() => {
            setCurrentModal(SIGNUP_SCREEN.ONBOARDING);
          }, 1000);
        } else {
           if (!isSignupCompleted) {
            isSignupCompleted = true;
            await handleOnCompleteSignUpProcess();
          }
        }
      }

      // Check if both email and phone are verified or if there's only one scope in the sign-up process
      else if (
        (newData?.email?.isVerified && newData?.phone?.isVerified) ||
        instanceDetails.signUp?.scope.length === 1
      ) {
        // Update the userResp state to mark the current key as verified
        setUserResp((prev) => {
          const newObj = shallowCopy(prev); // Create a shallow copy of the previous state

          if (newObj[key]) {
            newObj[key].isVerified = true; // Mark the current key as verified
          }

          ref.current = newObj;
          return newObj; // Return the updated state
        });

        // If conditions are met, set a timeout to update the current step to ACCOUNT_DETAILS after 1 second
        setTimeout(() => {
          setCurrentStep(SIGNUP_SCREEN.ACCOUNT_DETAILS);
        }, 1000);
      } else {
        if ((email || phone) && (!email?.isVerified || !phone?.isVerified)) {
          // If conditions are not met, set a timeout to update the current step to VERIFY and change the verification method
          setTimeout(() => {
            const pendingKey = key === "phone" ? "email" : "phone"; // Determine the pending key to verify
            setCurrentMethod(pendingKey); // Update the current verification method
            if (webcomponentProps?.iscombinedinput) {
              setForm((prev) => {
                const updatedForm = prev.map((field) =>
                  field.name === pendingKey
                    ? { ...field, disabled: false }
                    : field
                );
                return filterFormByScope(updatedForm);
              });
              setCurrentStep(SIGNUP_SCREEN.ACCOUNT_DETAILS); // Update the current step to VERIFY
            } else {
              setCurrentModal(SIGNUP_SCREEN.VERIFY); // Update the current step to VERIFY
            }
          }, 1000);
        }

        // Update the userResp state to mark the current key as verified
        setUserResp((prev) => {
          const newObj = shallowCopy(prev); // Create a shallow copy of the previous state

          if (newObj[key]) {
            newObj[key].isVerified = true; // Mark the current key as verified
          }

          ref.current = newObj;
          return newObj; // Return the updated state
        });
      }
    } else if (resp.status === "REJECTED") {
      // If the response status is "REJECTED", update the current step to NOTVERIFIED
      setCurrentModal(SIGNUP_SCREEN.NOTVERIFIED);
    }
  };

  const handleSuccessEvent = useCallback(
    async (resp: any) => {
      try {
        setLoadingRegisterationTracking({
          label: "Generating Token",
          loading: true,
        });
        onStepChangeEvent({
          type: "generating-token",
          from: "signup",
        });
        await onSuccessEvent(resp).finally(() => {
          setIsCardLoading(false);
          setLoadingRegisterationTracking({ label: "", loading: false });
        });
      } catch (error) {
        onErrorEvent({
          type: "failed-generating-token",
          from: "signup",
          error: error,
        });
      } finally {
        setLoadingRegisterationTracking({ label: "", loading: false });
      }
    },
    [onErrorEvent, onStepChangeEvent, onSuccessEvent]
  );

  useEffect(() => {
    if (currentScreen !== "signup") {
      return;
    }
    if (!userResp._id) {
      // eslint-disable-next-line no-console
      console.error("UserId not found");
      return;
    }

    const eventsRef = query(firebaseRef(db, userResp._id), limitToLast(1));

    const unsubscribe = onChildAdded(eventsRef, (snapshot) => {
      const data = snapshot.val();
      const resp = JSON.parse(data.data || "{}");

      if (resp.event === "approvalStatus") {
        handleOnApproveStatus(resp.payload);
        return;
      } else if (resp.event === "signupCompleted") {
        handleSuccessEvent(resp.payload);
        return;
      } 
    });

    // ✅ Clean up listener
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreen, userResp._id]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (formatTimeoutRef.current) {
        clearTimeout(formatTimeoutRef.current);
      }
    };
  }, []);

  const handleCombinedInputChange = useCallback(
    (formData: any) => {
      console.log({ formData });
      setForm(() => {
        const newObj = shallowCopy(form) ?? form; // Create a shallow copy of the form state
        const updatedForm = newObj.map((el: any) => {
          if (el.name === formData.name) {
            return {
              ...el,
              value: formData.value,
              isError: formData.isError,
              isHidden: false,
              ...(formData.name === "phone" && {
                countryCode: formData.countryCode,
              }),
            };
          } else {
            return {
              ...el,
              value: "",
              isHidden: false,
            };
          }
        });
        return filterFormByScope(updatedForm); // Return updated form state
      });
    },
    [form, setForm, filterFormByScope]
  );

  return {
    submitCred, // Function for submitting credentials
    handleSubmitRegistration, // Function for handling registration submission
    form, // Form state
    handleChangeCountry, // Handler for changing country code
    handleChange, // Handler for form input changes
    setForm, // Setter function for updating form state
    setChecked, // Setter function for updating checked state
    checked, // State for whether terms and conditions checkbox is checked
    loadingRegistration, // State for loading status during registration
    loadingSubmitCredential, // State for loading status during submit credential
    currentStep, //State for currentStep
    setCurrentStep, // State for SetCurrentStep,
    handleResend, // Function for resend link  handleResend
    loadingResend, // State for loading status during resend
    handleOnCompleteSignUpProcess,
    handleSocialSignUp,
    currentMethod,
    handleCombinedInputChange,
    setCurrentModal,
    currentModal,
    manageDefaultUser,
    loading,
    handleSignUpSSO: handleSSO,
    loadingRegisterationTracking,
    isCardLoading,
    setIsCardLoading,
    setUserResp
  };
};
